import {
    Component,
    OnInit,
    AfterViewInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Input,
} from '@angular/core';
import { Subject } from 'rxjs';
import { StripeElements } from '@stripe/stripe-js';
import { StripeElementsService } from 'src/app/shared/stripe-elements/stripe-elements.service';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { CheckoutFormService } from '../../checkout-form-service/checkout-form.service';

@Component({
    selector: 'ols-stripe-manual',
    templateUrl: './stripe-manual.component.html',
    styleUrls: ['./stripe-manual.component.scss'],
})
export class StripeManualComponent
    implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    private ngUnsubscribe: Subject<void> = new Subject();

    @Input() amount = 0;
    @Input() currency = 'usd';

    private stripePaymentElements: StripeElements;
    private paymentElement: any;
    private isInitialized = false;
    public loading = true;
    private currencyCode = '';
    private uniqueElementId: string;

    constructor(
        private stripeElementsService: StripeElementsService,
        private notificationService: NotificationService,
        private checkoutFormService: CheckoutFormService,
        private formService: CheckoutFormService
    ) {
        // Generate a unique ID for this component instance
        this.uniqueElementId = `stripe-payment-element-${this.generateUniqueId()}`;
    }

    async ngOnInit() {
        this.currencyCode = 'usd';
    }

    private generateUniqueId(): string {
        return Math.random().toString(36).substring(2, 15);
    }

    get elementId(): string {
        return this.uniqueElementId;
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only reinitialize if the component is visible and inputs have actually changed
        if (
            this.isInitialized &&
            changes &&
            (changes.amount || changes.currency)
        ) {
            console.log('StripeManualComponent: Input changed, reinitializing');
            this.initializeStripeElements();
        }
    }

    ngAfterViewInit() {
        this.initializeStripeElements();
    }

    public async reinitialize() {
        await this.initializeStripeElements();
    }

    public resetAndReinitialize() {
        // Force cleanup and reinitialize - useful when switching payment modes
        this.checkoutFormService.clearStripeElements();
        this.initializeStripeElements();
    }

    ngOnDestroy() {
        this.cleanupStripeElements();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private cleanupStripeElements() {
        if (this.paymentElement) {
            try {
                this.paymentElement.unmount();
            } catch (error) {
                console.warn('Error unmounting Stripe payment element:', error);
            }
            this.paymentElement = null;
        }
        this.stripePaymentElements = null;
        this.isInitialized = false;
    }

    private async initializeStripeElements() {
        // Clean up any existing elements first
        this.cleanupStripeElements();

        try {
            this.loading = true;
            const stripeElements = await this.stripeElementsService.createStripeElementsForSetup(
                this.currencyCode
            );
            if (stripeElements) {
                this.stripePaymentElements = stripeElements;
                this.paymentElement = stripeElements.create('payment', {});

                // Ensure the element container exists before mounting
                const elementContainer = document.getElementById(
                    this.uniqueElementId
                );
                if (elementContainer) {
                    this.paymentElement.mount(`#${this.uniqueElementId}`);
                    this.checkoutFormService.setStripePaymentElements(
                        stripeElements
                    );
                    this.isInitialized = true;
                    this.loading = false;
                } else {
                    console.error(
                        'Stripe payment element container not found:',
                        this.uniqueElementId
                    );
                    this.displayInitializingErrorMsg();
                }
            } else {
                this.displayInitializingErrorMsg();
            }
        } catch (error) {
            console.error('Error initializing Stripe elements:', error);
            this.displayInitializingErrorMsg();
        }
    }

    private displayInitializingErrorMsg() {
        this.notificationService.showFailure(
            'Error initializing credit card form, please refresh the page'
        );
        this.loading = false;
    }
}
